import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Loader2, ExternalLink, FileText, BookOpen } from 'lucide-react';
import { useModuleDetailsQuery, CourseModule } from '../../../api/studentCoursesApi';
import ModuleCompleteButton from '../common/ModuleCompleteButton';
import { tokenService } from '../../../services/TokenService';
import './LessonModuleStyles.css';

interface LessonModuleContentProps {
  contextId: string;
  moduleId: string;
  module: CourseModule | null;
}

const LessonModuleContent: React.FC<LessonModuleContentProps> = ({ contextId, moduleId, module }) => {
  const token = tokenService.getToken();
  const [lessonContent, setLessonContent] = useState<string>('');
  const [isFetchingContent, setIsFetchingContent] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const { data: details, isLoading, error } = useModuleDetailsQuery(
    contextId,
    moduleId,
    'lesson',
    token,
    {
      enabled: !!contextId && !!moduleId,
    }
  );

  // Fetch lesson content
  useEffect(() => {
    const fetchLessonContent = async () => {
      // Try to get content from API response
      if (details) {
        const apiContent = details.intro || details.description || details.content || '';
        if (apiContent) {
          setLessonContent(apiContent);
          return;
        }
      }

      // If no content from API, try to fetch from lesson pages if available
      if (module?.contents && module.contents.length > 0) {
        const htmlFile = module.contents.find(c => c.filename === 'index.html' || c.mimetype?.includes('text/html'));
        if (htmlFile?.fileurl) {
          setIsFetchingContent(true);
          setFetchError(null);
          
          try {
            const response = await fetch(htmlFile.fileurl);
            if (!response.ok) {
              throw new Error(`Failed to fetch content: ${response.statusText}`);
            }
            const html = await response.text();
            setLessonContent(html);
          } catch (err) {
            console.error('Error fetching lesson content:', err);
            setFetchError(err instanceof Error ? err.message : 'Failed to load content');
          } finally {
            setIsFetchingContent(false);
          }
        }
      }
    };

    if (details || module?.contents) {
      fetchLessonContent();
    }
  }, [details, module?.contents]);

  const handleOpenInMoodle = () => {
    if (module?.url) {
      window.open(module.url, '_blank');
    }
  };

  // Loading State
  if (isLoading || isFetchingContent) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-3" />
          <span className="text-gray-600">
            {isFetchingContent ? 'Loading lesson content...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  // Error State
  if (error || fetchError) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 mb-1">
              Failed to load lesson content
            </h3>
            <p className="text-sm text-red-700">
              {fetchError || (error instanceof Error ? error.message : 'An unknown error occurred')}
            </p>
            {module?.url && (
              <Button
                onClick={handleOpenInMoodle}
                variant="outline"
                size="sm"
                className="mt-3"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in Moodle
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Lesson Content
  if (lessonContent) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 max-h-[calc(100vh-7.8rem)] overflow-y-auto">
          <div
            className="lesson-content"
            dangerouslySetInnerHTML={{ __html: lessonContent }}
          />
        </div>
        <div className="border-t border-gray-200 p-4 flex items-center justify-between">
          {module?.url ? (
            <Button
              onClick={handleOpenInMoodle}
              variant="outline"
              size="sm"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Full Lesson in Moodle
            </Button>
          ) : <div />}
          <ModuleCompleteButton contextId={contextId} moduleId={moduleId} variant="footer" />
        </div>
      </div>
    );
  }

  // No Content State
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12">
      <div className="text-center">
        <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 mb-4">No lesson content available</p>
        {module?.url && (
          <Button onClick={handleOpenInMoodle} variant="outline">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open in Moodle
          </Button>
        )}
      </div>
    </div>
  );
};

export default LessonModuleContent;
